import React, { useState, useEffect } from 'react';
import { Shield, Send, X, AlertCircle, Clock, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { incidentsApi, IncidentDetailResponse } from '../../lib/api';
import { DEFENSE_SUBMITTED_EVENT } from '../../hooks/usePendingDefenses';
import toast from 'react-hot-toast';

interface DefenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  incidentId: string;
  onSuccess?: () => void;
}

export const DefenseModal: React.FC<DefenseModalProps> = ({
  isOpen,
  onClose,
  incidentId,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [incident, setIncident] = useState<IncidentDetailResponse | null>(null);
  const [defenseText, setDefenseText] = useState('');
  const [defenseLink, setDefenseLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPastDeadline, setIsPastDeadline] = useState(false);

  useEffect(() => {
    if (isOpen && incidentId) {
      setDefenseText('');
      setDefenseLink('');
      setIsPastDeadline(false);
      fetchIncident();
    }
  }, [isOpen, incidentId]);

  const fetchIncident = async () => {
    try {
      setLoading(true);
      const data = await incidentsApi.get(incidentId);
      setIncident(data);
      
      // Check deadline
      const deadline = new Date(data.incident.deadline_at);
      if (deadline < new Date()) {
        setIsPastDeadline(true);
      }
    } catch (err: any) {
      toast.error(err.detail || 'Erro ao carregar incidente');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    let cleanText = defenseText.trim();
    let cleanLink = defenseLink.trim();

    if (!cleanText || !cleanLink) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!/^https?:\/\//i.test(cleanLink)) {
      cleanLink = `https://${cleanLink}`;
    }

    try {
      setLoading(true);
      await incidentsApi.submitDefense(incidentId, {
        defense_text: cleanText,
        defense_link: cleanLink
      });
      toast.success('Defesa enviada com sucesso!');
      // Remove imediatamente o alerta/banner de pendência nos consoles
      window.dispatchEvent(new CustomEvent(DEFENSE_SUBMITTED_EVENT));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err.detail || err.message || 'Erro ao enviar defesa');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" 
        onClick={onClose} 
      />
      
      {/* Modal Container */}
      <div className="relative w-full max-w-2xl bg-zinc-950 rounded-3xl border border-zinc-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">
        {/* Accent Bar */}
        <div className="h-1 w-full bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.5)]" />
        
        <div className="p-6 overflow-y-auto custom-scrollbar">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-600/10 rounded-2xl flex items-center justify-center text-blue-500 border border-blue-600/20 rotate-3">
                <Shield size={28} className="-rotate-3" />
              </div>
              <div>
                <h2 className="font-condensed text-3xl font-black italic uppercase tracking-tighter text-white leading-none">
                  Processo de Defesa
                </h2>
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mt-1">
                  ID DO INCIDENTE: #{incidentId.substring(0, 8)}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 text-zinc-500 hover:text-white rounded-full hover:bg-white/5 transition-colors border border-transparent hover:border-zinc-800"
            >
              <X size={20} />
            </button>
          </div>

          {/* Incident Context */}
          {incident && (
            <div className="bg-zinc-900 rounded-2xl p-5 mb-8 border border-zinc-800 relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-3">
                 <span className="flex items-center gap-1.5 text-[9px] text-zinc-400 bg-zinc-950 px-2.5 py-1 rounded-full border border-zinc-800 font-mono group-hover:border-blue-500/30 transition-colors">
                   <Clock size={10} className="text-blue-500" />
                   {t('pilot_console.defense_deadline_label')}: {incident.incident.deadline_at ? new Date(incident.incident.deadline_at).toLocaleString() : '—'}
                 </span>
               </div>

               <div className="mb-3">
                 <span className="text-blue-500 font-black text-[10px] uppercase tracking-[0.2em] italic">
                   RELATO DA DENÚNCIA
                 </span>
               </div>
               
               <p className="text-zinc-400 text-sm leading-relaxed italic border-l-2 border-blue-600/30 pl-4 py-1">
                 "{incident.incident.description}"
               </p>

               {incident.incident.evidence_url && (
                 <div className="mt-4 pt-4 border-t border-zinc-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                   <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
                     <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                     Evidência Anexada pelo Solicitante
                   </div>
                   <a
                     href={incident.incident.evidence_url}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 text-xs font-mono font-bold border border-blue-600/30 transition-all hover:border-blue-500/50 group/link"
                   >
                     <ExternalLink size={14} className="group-hover/link:scale-110 transition-transform" />
                     Abrir Prova / Vídeo da Denúncia
                   </a>
                 </div>
               )}
            </div>
          )}

          {/* Aviso de prazo — o formulário permanece ATIVO; em produção o backend recusa envios fora da janela */}
          {isPastDeadline && (
            <div className="bg-amber-500/5 border border-amber-500/25 rounded-2xl p-5 mb-8 flex items-start gap-4 animate-in slide-in-from-top-5">
              <AlertCircle size={22} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-amber-500 font-black italic uppercase tracking-tighter text-sm mb-1">
                  {t('pilot_console.defense_deadline_ended')}
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  {incident?.incident?.deadline_at
                    ? t('pilot_console.defense_deadline_warning', { date: new Date(incident.incident.deadline_at).toLocaleString() })
                    : t('pilot_console.defense_deadline_warning_generic')}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-8 animate-in slide-in-from-bottom-5">
              {/* Defense Text */}
              <div className="space-y-3">
                <div className="flex justify-between items-end">
                  <label className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-black italic">
                    Sua Versão dos Fatos
                  </label>
                  <span className="text-[9px] text-zinc-600 font-mono">OBRIGATÓRIO</span>
                </div>
                <div className="relative group">
                  <textarea
                    value={defenseText}
                    onChange={(e) => setDefenseText(e.target.value)}
                    placeholder="Descreva detalhadamente sua perspectiva do incidente..."
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-5 text-zinc-300 text-sm focus:border-blue-500 outline-none min-h-[180px] transition-all resize-none group-hover:border-zinc-700"
                  />
                  <div className="absolute bottom-4 right-4 pointer-events-none text-zinc-800">
                    <Shield size={40} strokeWidth={1} />
                  </div>
                </div>
              </div>

              {/* Defense Link */}
              <div className="space-y-3">
                <div className="flex justify-between items-end">
                  <label className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-black italic">
                    Link da Prova (Vídeo/Onboard/Arquivo)
                  </label>
                  <span className="text-[9px] text-zinc-600 font-mono">MANDATÓRIO</span>
                </div>
                <input
                  type="text"
                  inputMode="url"
                  value={defenseLink}
                  onChange={(e) => setDefenseLink(e.target.value)}
                  placeholder="https://youtube.com/..., Google Drive, Twitch, Streamable, OneDrive, etc."
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-5 py-4 text-zinc-300 text-sm focus:border-blue-500 outline-none transition-all group-hover:border-zinc-700 font-mono"
                />
                <p className="text-[9px] text-zinc-600 italic">
                  * Aceita links do YouTube, Google Drive, Twitch, Streamable, OneDrive, DropBox ou qualquer link de vídeo/comprovante.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-6 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-5 text-zinc-500 hover:text-white font-black italic uppercase tracking-widest text-[11px] transition-all border border-transparent hover:border-zinc-800 rounded-2xl"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !defenseText.trim() || !defenseLink.trim()}
                  className="flex-[2] bg-blue-600 hover:bg-blue-500 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed text-white py-5 rounded-2xl font-black italic uppercase tracking-widest text-xs flex items-center justify-center gap-3 transition-all shadow-[0_10px_20px_rgba(37,99,235,0.2)] hover:shadow-[0_15px_30px_rgba(37,99,235,0.3)] active:scale-95"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                       <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                       Processando...
                    </div>
                  ) : (
                    <>
                      Submeter Defesa Oficial
                      <Send size={16} className="-mt-0.5" />
                    </>
                  )}
                </button>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};
